import { getMCPServer, updateMCPServer } from '@/service/mcpServerService';
import Utils from '@/common/utils/utils';
import McpServerApi from '@/common/constant/api/McpServerApi';
import Logger from '@/common/utils/Logger';

/**
 * Error codes from the backend that can be mapped to specific form fields.
 */
const FIELD_ERROR_CODE_MAP = {
    MCP_ERR_CATALOG_NAME_DUPLICATE: 'mcpName',
    MCP_ERR_PACKAGE_NAME_DUPLICATE: 'mcpPackageName'
};

/**
 * Error code returned when the item has been deleted by another Root Admin.
 * Maps to COMMSG030 dialog on the frontend.
 */
const DATA_CHANGED_ERROR_CODE = 'MCP_ERR_DATA_CHANGED';

/**
 * Parse backend field errors from the response payload into react-hook-form
 * compatible setError format: [{ field, messageCode, params }].
 *
 * @param {object|null} payload - response.payload from backend
 * @returns {Array<{field: string, messageCode: string, params: object|null}>}
 */
const parseFieldErrors = (payload) => {
    if (!payload || !Array.isArray(payload.fieldErrors)) return [];

    return payload.fieldErrors.map(({ field, code, params }) => ({
        field,
        messageCode: code,
        params: params || null
    }));
};

/**
 * Map a top-level error code to a field error if applicable.
 * Used when the error is returned as a top-level `error` string rather than fieldErrors.
 *
 * @param {string} errorCode
 * @returns {Array<{field: string, messageCode: string}>}
 */
const mapTopLevelErrorToField = (errorCode) => {
    if (!errorCode) return [];
    const field = FIELD_ERROR_CODE_MAP[errorCode];
    if (!field) return [];
    return [{ field, messageCode: errorCode }];
};

/**
 * Fetch the full Local MCP Catalog list for the ローカルMCP一覧 screen (spec 4.1).
 *
 * Returns one of two result shapes:
 *  - { ok: true, items }            — success, list of catalog items (createdAt ASC)
 *  - { ok: false, systemError }     — auth/infra error, show generic toast
 *
 * The response does NOT include version history / snapshots — that is a separate method.
 *
 * @param {string} corporationInuse - Root corp ID from localStorage
 * @param {string} userId           - authenticated user ID from localStorage
 * @returns {Promise<object>}
 */
export const getMcpCatalogList = async (corporationInuse, userId) => {
    try {
        const response = await getMCPServer({
            corporationInuse,
            userId,
            getMethod: McpServerApi.MCP_CATALOG_GET_METHOD.GET_LIST
        });

        if (!response || !response.success) {
            return { ok: false, systemError: response?.error || 'MCP_ERR_INTERNAL' };
        }

        const payload = Utils.convertJSONDataToObject(response.payload);
        const items = !Utils.isEmptyArray(payload?.listItem) ? payload.listItem : [];
        return { ok: true, items };
    } catch (err) {
        Logger.error('getMcpCatalogList error:', err);
        return { ok: false, systemError: 'MCP_ERR_INTERNAL' };
    }
};

/**
 * Create a new MCP Catalog item.
 *
 * Returns one of three result shapes:
 *  - { ok: true, item }             — success, use item to append to list
 *  - { ok: false, fieldErrors }     — validation/duplicate errors, map to form
 *  - { ok: false, systemError }     — auth/infra error, show generic toast
 *
 * @param {object} catalogData   - normalized catalog fields from the form
 * @param {string} corporationInuse - Root corp ID from localStorage
 * @param {string} userId        - authenticated user ID from localStorage
 * @returns {Promise<object>}
 */
export const createMcpCatalog = async (catalogData, corporationInuse, userId) => {
    try {
        const requestDto = {
            corporationInuse,
            userId,
            handleMethod: McpServerApi.MCP_CATALOG_UPDATE_METHOD.CREATE,
            mcpCatalogData: catalogData
        };

        const response = await updateMCPServer(requestDto);

        // Null response = network/GraphQL level error
        if (!response) {
            return { ok: false, systemError: 'MCP_ERR_INTERNAL' };
        }

        const payload = Utils.convertJSONDataToObject(response.payload);

        if (response.success) {
            const item = payload?.item || null;
            return { ok: true, item };
        }

        // Backend returned failure — check for field-level errors first
        const fieldErrors = parseFieldErrors(payload);
        if (!Utils.isEmptyArray(fieldErrors)) {
            return { ok: false, fieldErrors };
        }

        // Then try top-level error code → field mapping
        const mappedErrors = mapTopLevelErrorToField(response.error);
        if (!Utils.isEmptyArray(mappedErrors)) {
            return { ok: false, fieldErrors: mappedErrors };
        }

        // System-level error (auth, infra, etc.)
        return { ok: false, systemError: response.error || 'MCP_ERR_INTERNAL' };

    } catch (err) {
        Logger.error('createMcpCatalog error:', err);
        return { ok: false, systemError: 'MCP_ERR_INTERNAL' };
    }
};

/**
 * Update an existing MCP Catalog item.
 *
 * Returns one of four result shapes:
 *  - { ok: true, item }             — success, replace item in list
 *  - { ok: false, fieldErrors }     — validation/duplicate errors, map to form inline
 *  - { ok: false, dataChanged }     — item deleted by another admin (COMMSG030)
 *  - { ok: false, systemError }     — auth/infra error, show generic toast
 *
 * @param {object} catalogData      - catalog fields from the form (must include id)
 * @param {string} corporationInuse - Root corp ID from localStorage
 * @param {string} userId           - authenticated user ID from localStorage
 * @returns {Promise<object>}
 */
export const updateMcpCatalog = async (catalogData, corporationInuse, userId) => {
    try {
        const requestDto = {
            corporationInuse,
            userId,
            handleMethod: McpServerApi.MCP_CATALOG_UPDATE_METHOD.UPDATE,
            mcpCatalogData: catalogData
        };

        const response = await updateMCPServer(requestDto);

        if (!response) {
            return { ok: false, systemError: 'MCP_ERR_INTERNAL' };
        }

        const payload = Utils.convertJSONDataToObject(response.payload);

        if (response.success) {
            const item = payload?.item || null;
            return { ok: true, item };
        }

        // Item no longer exists — show COMMSG030
        if (response.error === DATA_CHANGED_ERROR_CODE) {
            return { ok: false, dataChanged: true };
        }

        // Field-level errors
        const fieldErrors = parseFieldErrors(payload);
        if (!Utils.isEmptyArray(fieldErrors)) {
            return { ok: false, fieldErrors };
        }

        // Top-level error → field mapping (e.g. duplicate codes)
        const mappedErrors = mapTopLevelErrorToField(response.error);
        if (!Utils.isEmptyArray(mappedErrors)) {
            return { ok: false, fieldErrors: mappedErrors };
        }

        return { ok: false, systemError: response.error || 'MCP_ERR_INTERNAL' };

    } catch (err) {
        Logger.error('updateMcpCatalog error:', err);
        return { ok: false, systemError: 'MCP_ERR_INTERNAL' };
    }
};

/**
 * Fetch the version history snapshots for a MCP Catalog item.
 * Called when opening the edit form to populate 「バージョン更新履歴」.
 *
 * Returns one of three result shapes:
 *  - { ok: true, versions }         — success, array of { version, date, updatedBy }
 *  - { ok: false, dataChanged }     — item deleted (COMMSG030)
 *  - { ok: false, systemError }     — infra error (form should still open with empty history)
 *
 * @param {string} id               - catalog item ID
 * @param {string} corporationInuse - Root corp ID from localStorage
 * @param {string} userId           - authenticated user ID from localStorage
 * @returns {Promise<object>}
 */
/**
 * Delete a Local MCP Catalog item on the backend.
 *
 * Returns one of three normalized result shapes:
 *  - { ok: true }                       — backend confirmed deletion
 *  - { ok: false, dataChanged: true }   — item no longer exists (COMMSG030)
 *  - { ok: false, systemError }         — invalid input / auth / infra error (COMMSG001)
 *
 * @param {string} id               - target Local MCP Catalog item ID
 * @param {string} corporationInuse - Root corp ID from localStorage
 * @param {string} userId           - authenticated user ID from localStorage
 * @returns {Promise<object>}
 */
export const deleteMcpCatalog = async (id, corporationInuse, userId) => {
    try {
        // Input guard: all three params must be non-empty strings
        const trimmedId = typeof id === 'string' ? id.trim() : '';
        const trimmedCorp = typeof corporationInuse === 'string' ? corporationInuse.trim() : '';
        const trimmedUser = typeof userId === 'string' ? userId.trim() : '';

        if (!trimmedId || !trimmedCorp || !trimmedUser) {
            return { ok: false, systemError: 'MCP_ERR_INTERNAL' };
        }

        const requestDto = {
            corporationInuse: trimmedCorp,
            userId: trimmedUser,
            handleMethod: McpServerApi.MCP_CATALOG_UPDATE_METHOD.DELETE,
            mcpCatalogData: { id: trimmedId }
        };

        const response = await updateMCPServer(requestDto);

        // Null response = network/GraphQL level error
        if (!response) {
            return { ok: false, systemError: 'MCP_ERR_INTERNAL' };
        }

        // Item no longer exists — show COMMSG030
        if (response.error === DATA_CHANGED_ERROR_CODE) {
            return { ok: false, dataChanged: true };
        }

        if (response.success) {
            return { ok: true };
        }

        // Other non-success response
        return { ok: false, systemError: response.error || 'MCP_ERR_INTERNAL' };

    } catch (err) {
        Logger.error('deleteMcpCatalog error:', err);
        return { ok: false, systemError: 'MCP_ERR_INTERNAL' };
    }
};

export const getMcpCatalogSnapshots = async (id, corporationInuse, userId, limit = 10, lastEvaluatedKey = null) => {
    try {
        const response = await getMCPServer({
            corporationInuse,
            userId,
            getMethod: McpServerApi.MCP_CATALOG_GET_METHOD.GET_SNAPSHOTS,
            queryParams: JSON.stringify({ parentId: id, limit, lastEvaluatedKey })
        });

        if (!response) {
            return { ok: false, systemError: 'MCP_ERR_INTERNAL' };
        }

        if (response.error === DATA_CHANGED_ERROR_CODE) {
            return { ok: false, dataChanged: true };
        }

        if (!response.success) {
            return { ok: false, systemError: response.error || 'MCP_ERR_INTERNAL' };
        }

        const payload = Utils.convertJSONDataToObject(response.payload);
        const versions = !Utils.isEmptyArray(payload?.versions) ? payload.versions : [];
        const nextCursor = payload?.lastEvaluatedKey || null;
        return { ok: true, versions, lastEvaluatedKey: nextCursor };

    } catch (err) {
        Logger.error('getMcpCatalogSnapshots error:', err);
        return { ok: false, systemError: 'MCP_ERR_INTERNAL' };
    }
};
